/**
 * ActivateAccount — First-login password setup page.
 *
 * Flow:
 *   1. Extract ?token= from URL.
 *   2. Call GET /api/auth/activate-account?token=… to validate token + fetch user info.
 *   3. Show email/name (read-only) + password + confirm-password form.
 *   4. POST /api/auth/activate-account to set password and activate account.
 *   5. Redirect to /login with a success toast.
 *
 * Branding: Prashasta Events — Powered by ARGOV Technologies
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBrand } from "@/contexts/BrandContext";
import { toast } from "sonner";
import { Zap, Eye, EyeOff, AlertCircle, CheckCircle, Loader2, ShieldCheck } from "lucide-react";

const MIN_PW_LENGTH = 15;

function PasswordStrength({ password }) {
  if (!password) return null;
  const checks = [
    password.length >= MIN_PW_LENGTH,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ["", "Too short", "Weak", "Fair", "Strong"];
  const colors = ["", "bg-rose-400", "bg-amber-400", "bg-lime-400", "bg-emerald-500"];
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= score ? colors[score] : "bg-[#E5E5E5]"}`} />
        ))}
      </div>
      <p className={`text-xs ${score >= 3 ? "text-emerald-600" : "text-[#706E6B]"}`}>{labels[score]}</p>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const brand = useBrand();

  const [status, setStatus] = useState("loading"); // loading | valid | invalid | success
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    fetch(`${API}/auth/activate-account?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail || "Invalid or expired activation link.");
          setStatus("invalid");
          return;
        }
        const data = await res.json();
        setUserInfo(data);
        setStatus("valid");
      })
      .catch(() => {
        setError("Could not verify activation link. Please try again.");
        setStatus("invalid");
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPwd) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < MIN_PW_LENGTH) {
      setError(`Password must be at least ${MIN_PW_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/activate-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, confirm_password: confirmPwd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Activation failed. Please try again.");
        return;
      }
      setStatus("success");
      toast.success("Account activated! You can now sign in.");
      setTimeout(() => navigate("/login"), 2000);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F3F3] px-4">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded bg-[#0176D3] flex items-center justify-center mb-4">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#181818] font-sans tracking-tight">
            {brand.productName}
          </h1>
          <p className="text-sm text-[#706E6B] mt-1 font-mono">
            Powered by {brand.companyName}
          </p>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>

          {/* Loading */}
          {status === "loading" && (
            <div className="flex flex-col items-center py-8 gap-3">
              <Loader2 className="w-8 h-8 text-[#0176D3] animate-spin" />
              <p className="text-sm text-[#706E6B]">Verifying your activation link…</p>
            </div>
          )}

          {/* Invalid / Expired */}
          {status === "invalid" && (
            <div className="flex flex-col items-center py-6 gap-4 text-center">
              <AlertCircle className="w-10 h-10 text-rose-500" />
              <h2 className="text-base font-semibold text-[#181818]">Activation Link Invalid</h2>
              <p className="text-sm text-[#706E6B]">
                {error || "This activation link is invalid or has expired."}
              </p>
              <p className="text-xs text-[#706E6B]">
                Please contact your Tenant Administrator to request a new invitation.
              </p>
              <button
                onClick={() => navigate("/login")}
                data-testid="activate-back-to-login"
                className="mt-2 px-6 py-2 rounded bg-[#0176D3] text-white text-sm font-semibold hover:bg-[#014486] transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          )}

          {/* Success */}
          {status === "success" && (
            <div className="flex flex-col items-center py-6 gap-4 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
              <h2 className="text-base font-semibold text-[#181818]">Account Activated</h2>
              <p className="text-sm text-[#706E6B]">
                Your password has been set. Redirecting to sign in…
              </p>
            </div>
          )}

          {/* Activation form */}
          {status === "valid" && userInfo && (
            <>
              <h2 className="text-lg font-semibold text-[#181818] mb-1">Set your password</h2>
              <p className="text-sm text-[#706E6B] mb-6">Create a secure password to activate your account.</p>

              {/* User info (read-only) */}
              <div className="mb-5 p-4 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-[#706E6B] font-mono uppercase tracking-widest">Name</span>
                  <span className="text-[#181818] font-medium">{userInfo.name}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#706E6B] font-mono uppercase tracking-widest">Email</span>
                  <span className="text-[#181818] font-medium">{userInfo.email}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#706E6B] font-mono uppercase tracking-widest">Role</span>
                  <span className="text-[#0176D3] font-medium">{userInfo.role}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="activate-form">
                <div>
                  <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      data-testid="activate-password-input"
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 15 characters"
                      required
                      className="w-full px-4 py-2.5 pr-10 rounded border border-[#C9C9C9] bg-white text-[#181818] text-sm placeholder:text-[#C9C9C9] focus:outline-none focus:border-[#1B96FF] focus:ring-1 focus:ring-[#1B96FF] transition-colors"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] hover:text-[#444]">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                  <p className="text-xs text-[#706E6B] mt-1.5 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-[#0176D3]" />
                    Min {MIN_PW_LENGTH} characters · Passphrases with spaces are allowed
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <input
                      data-testid="activate-confirm-input"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPwd}
                      onChange={e => setConfirmPwd(e.target.value)}
                      placeholder="Re-enter password"
                      required
                      className="w-full px-4 py-2.5 pr-10 rounded border border-[#C9C9C9] bg-white text-[#181818] text-sm placeholder:text-[#C9C9C9] focus:outline-none focus:border-[#1B96FF] focus:ring-1 focus:ring-[#1B96FF] transition-colors"
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] hover:text-[#444]">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="activate-error" className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="activate-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 rounded bg-[#0176D3] text-white font-semibold text-sm hover:bg-[#014486] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {submitting ? "Activating…" : "Activate Account"}
                </button>
              </form>
            </>
          )}

        </div>

        <p className="text-center text-xs text-[#706E6B] mt-6">
          {brand.copyrightText}
        </p>
      </div>
    </div>
  );
}
